import type { MergedTenant } from "@offstreetparking/portal-flow/types/entry-points";
import type { PortalV2RegistrationSetting, PortalV2Tenant } from "~/services/portalV2Api/types";

type P3Tenant = {
	id: number;
	name: string;
	discoverable: number;
	brandingFileUrl: string | null;
};

export function mergeTenants({
	p3Tenants,
	p2Tenants,
	p2RegSettings,
}: {
	p3Tenants: P3Tenant[];
	p2Tenants: PortalV2Tenant[];
	p2RegSettings: PortalV2RegistrationSetting[];
}): MergedTenant[] {
	const map = new Map<number, MergedTenant>();

	for (const tenant of p3Tenants) {
		map.set(tenant.id, {
			id: tenant.id,
			name: tenant.name,
			discoverable: tenant.discoverable,
			brandingFileUrl: tenant.brandingFileUrl,
			hasPermitTypes: true,
			hasRegSettings: false,
			regSettingIds: [],
		});
	}

	for (const tenant of p2Tenants) {
		const settingIds = tenant.registrationSettings.map((id) => Number(id));

		const availableSettingIds = settingIds.filter((settingId) => {
			const setting = p2RegSettings.find((s) => s.id === settingId);
			if (!setting?.wizardSettings.settingIsAvailable) {
				return false;
			}

			const isAtCapacity = tenant.disabledSettings?.some(
				(d) => d.settingId === settingId && d.maxRegistrationsReached,
			);
			return !isAtCapacity;
		});

		const hasAnySettings = settingIds.some((settingId) => {
			const setting = p2RegSettings.find((s) => s.id === settingId);
			return setting?.wizardSettings.settingIsAvailable;
		});

		if (hasAnySettings) {
			const existing = map.get(tenant.id);
			if (existing) {
				existing.hasRegSettings = true;
				existing.regSettingIds = availableSettingIds;
			} else {
				map.set(tenant.id, {
					id: tenant.id,
					name: tenant.name,
					discoverable: 1,
					brandingFileUrl: tenant.headerImage?.url ?? null,
					hasPermitTypes: false,
					hasRegSettings: true,
					regSettingIds: availableSettingIds,
				});
			}
		}
	}

	return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}
